import { formatCurrency } from "../../helpers/currency-formatting-util.js";
import { getCSSVariable } from "../../helpers/css-util.js";
import {
	makeNegativeGradient,
	makePositiveGradient,
} from "../../helpers/chart-util.js";

/**
 * The subclasses of this abstract class implement different ways
 * to populate a {@link ChartWidget}. They mainly build the series
 * out of the provided values.
 * This class implements a Strategy pattern.
 *
 * @abstract
 * @param {object[]} labels - The period names (anything with a
 *   toString() method) are labels of the x-axis
 */
export default class AbstractChartStrategy {
	constructor({ labels }) {
		this._labels = labels;
	}

	getConfiguration({ context, values }) {
		return {
			type: this._getChartType(),
			data: {
				labels: this._getLabels(),
				datasets: this._makeDatasets({
					values,
				}),
			},
			options: this._getChartOptions({ context }),
		};
	}

	getCssClass() {
		return "";
	}

	_getLabels() {
		return this._labels;
	}

	/**
	 * This method should return an array of dataset as supported
	 * by the chart.js library. See {@link https://www.chartjs.org/docs/3.9.1/samples/area/line-datasets.html}
	 * for an example of line datasets.
	 * You can also have a look at the concrete implementation of
	 * this class for more examples.
	 *
	 * @abstract
	 */
	_makeDatasets({ values: _ }) {
		throw new Error("Subclass responsibility");
	}

	/**
	 * Return the series type in the case only one type is used
	 * for all the series. If different types are used in the same
	 * graph, each type should be defined in the series dataset.
	 *
	 * @return {"area"|"bar"|"bubble"|"doughnut"|"pie"|"line"|"polarArea"|"radar"|"scatter"}
	 */
	_getChartType() {
		return undefined;
	}

	/**
	 * Return the top chart options.
	 */
	_getChartOptions({ context: _ } = {}) {
		return {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				tooltip: {
					callbacks: {
						label: ({ dataset, parsed }) => {
							let label = dataset.label || "";

							if (label) {
								label += ": ";
							}
							if (parsed.y !== null) {
								label += formatCurrency(parsed.y);
							}
							return label;
						},
					},
				},
			},
		};
	}

	_makePositiveGradient({ context }) {
		return makePositiveGradient({
			context,
			color: this._getPositiveGradientColor(),
			zeroPositionRatio: this._zeroPositionRatio,
			yAxisEndPoint: this._getPositiveEndPointForYAxis(),
		});
	}

	_makeNegativeGradient({ context }) {
		return makeNegativeGradient({
			context,
			color: this._getNegativeGradientColor(),
			zeroPositionRatio: this._zeroPositionRatio,
			yAxisEndPoint: this._getNegativeEndPointForYAxis(),
		});
	}

	_getNegativeColor() {
		return getCSSVariable("--chart-negative");
	}

	_getNegativeGradientColor() {
		return this._getNegativeColor();
	}

	_getPositiveEndPointForYAxis() {
		return getCSSVariable("--chart-positive-end-point");
	}

	_getNegativeEndPointForYAxis() {
		return getCSSVariable("--chart-negative-end-point");
	}
}
